import axios from 'axios';

import { auth, COLLABORATION_REST_API_URL } from '../../config';
import {
  ApplyTemplateToPageRequestType,
  ApplyTemplateToPageResponseType,
  ApplyTemplateToTabRequestType,
  ApplyTemplateToTabResponseType,
  CreateTabRequestType,
  CreateTabResponseType,
  DuplicateTabResponseType,
} from '@distribute/shared/api-types/pages';
import {
  CreateCommentRequestType,
  UpdateThreadStatusRequestType,
  UpdateCommentContentRequestType,
  UpdateMarkAsDeletedRequestType,
  ReplyThreadRequestType,
} from '@distribute/shared/api-types/comments';

import { DocumentContentItem } from '@distribute/shared/types';

const collaborationInstance = axios.create({
  baseURL: `${COLLABORATION_REST_API_URL}/parties/main`,
  timeout: 60000,
});

collaborationInstance.interceptors.request.use(async (config) => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    return config;
  }

  (config.headers as Record<string, string>)[
    'Authorization'
  ] = `Bearer ${token}`;

  return config;
});

type AddTabProps = {
  pageId: string;
  teamId: number;
  data: CreateTabRequestType;
};

type UpdateTabProps = {
  pageId: string;
  teamId: number;
  tabId: number;
  data: { name: string };
};

type GetContentProps = {
  pageId: string;
  teamId: number;
};

type DuplicateTabProps = {
  pageId: string;
  teamId: number;
  tabId: number;
};

type DeleteTabProps = {
  pageId: string;
  teamId: number;
  tabId: number;
};

type ApplyTemplateToPageProps = {
  pageId: string;
  teamId: number;
  data: Omit<ApplyTemplateToPageRequestType, 'userToken'>;
};

type ApplyTemplateToTabProps = {
  pageId: string;
  teamId: number;
  tabId: number;
  data: Omit<ApplyTemplateToTabRequestType, 'userToken'>;
};

type BaseCommentProps = {
  pageId: string;
  teamId: number;
};

type CreateCommentProps = CreateCommentRequestType & {
  teamId: number;
};

type UpdateThreadStatusProps = BaseCommentProps & {
  threadId: string;
  data: UpdateThreadStatusRequestType;
};

type UpdateCommentContentProps = BaseCommentProps & {
  threadId: string;
  commentId: string;
  data: UpdateCommentContentRequestType;
};

type MarkThreadAsDeletedProps = BaseCommentProps & {
  threadId: string;
  data: UpdateMarkAsDeletedRequestType;
};

type DeleteThreadProps = BaseCommentProps & {
  threadId: string;
};

type DeleteCommentProps = BaseCommentProps & {
  threadId: string;
  commentId: string;
};

type CreateCommentReplyProps = BaseCommentProps & {
  threadId: string;
  data: ReplyThreadRequestType;
};

export const collaborationApi = {
  async addTab({ pageId, teamId, data }: AddTabProps) {
    const response = await collaborationInstance.post<CreateTabResponseType>(
      `${pageId}/tab`,
      data,
      { params: { teamId } }
    );
    return response.data;
  },
  async deleteTab({ pageId, teamId, tabId }: DeleteTabProps) {
    const response = await collaborationInstance.delete(
      `${pageId}/tab/${tabId}`,
      { params: { teamId } }
    );
    return response.data;
  },
  async updateTab({ pageId, teamId, tabId, data }: UpdateTabProps) {
    const response = await collaborationInstance.patch<CreateTabResponseType>(
      `${pageId}/tab/${tabId}`,
      data,
      { params: { teamId } }
    );
    return response.data;
  },
  async duplicateTab({ pageId, teamId, tabId }: DuplicateTabProps) {
    const response = await collaborationInstance.post<DuplicateTabResponseType>(
      `${pageId}/tab/${tabId}/duplicate`,
      {},
      { params: { teamId } }
    );
    return response.data;
  },
  async getContent({ pageId, teamId }: GetContentProps) {
    const response = await collaborationInstance.get<DocumentContentItem[]>(
      `${pageId}/content`,
      { params: { teamId } }
    );
    return response.data;
  },
  async applyTemplateToPage({
    pageId,
    teamId,
    data,
  }: ApplyTemplateToPageProps) {
    const response =
      await collaborationInstance.post<ApplyTemplateToPageResponseType>(
        `${pageId}/apply-template`,
        data,
        { params: { teamId } }
      );
    return response.data;
  },
  async applyTemplateToTab({
    pageId,
    teamId,
    tabId,
    data,
  }: ApplyTemplateToTabProps) {
    const response =
      await collaborationInstance.post<ApplyTemplateToTabResponseType>(
        `${pageId}/tab/${tabId}/apply-template`,
        data,
        { params: { teamId } }
      );
    return response.data;
  },

  async createCommentThread({
    teamId,
    ...restData
  }: CreateCommentProps): Promise<{ threadId: string }> {
    const response = await collaborationInstance.post(
      `${restData.pageId}/comments/thread`,
      restData,
      { params: { teamId } }
    );

    return response.data;
  },

  async updateThreadStatus({
    teamId,
    threadId,
    pageId,
    data,
  }: UpdateThreadStatusProps) {
    const response = await collaborationInstance.patch(
      `${pageId}/comments/thread/${threadId}/status`,
      data,
      { params: { teamId } }
    );

    return response.data;
  },

  async updateCommentContent({
    teamId,
    commentId,
    threadId,
    pageId,
    data,
  }: UpdateCommentContentProps) {
    const response = await collaborationInstance.patch(
      `${pageId}/comments/thread/${threadId}/comment/${commentId}/content`,
      data,
      { params: { teamId } }
    );

    return response.data;
  },

  async markThreadAsDeleted({
    teamId,
    threadId,
    pageId,
    data,
  }: MarkThreadAsDeletedProps) {
    const response = await collaborationInstance.patch(
      `${pageId}/comments/thread/${threadId}/mark-deleted`,
      data,
      { params: { teamId } }
    );

    return response.data;
  },

  async deleteThread({ teamId, threadId, pageId }: DeleteThreadProps) {
    const response = await collaborationInstance.delete(
      `${pageId}/comments/thread/${threadId}`,
      { params: { teamId } }
    );

    return response.data;
  },

  async deleteComment({
    teamId,
    threadId,
    commentId,
    pageId,
  }: DeleteCommentProps) {
    const response = await collaborationInstance.delete(
      `${pageId}/comments/thread/${threadId}/comment/${commentId}`,
      { params: { teamId } }
    );

    return response.data;
  },

  async createCommentReply({
    teamId,
    threadId,
    pageId,
    data,
  }: CreateCommentReplyProps) {
    const response = await collaborationInstance.post(
      `${pageId}/comments/thread/${threadId}/reply/`,
      data,
      { params: { teamId } }
    );

    return response.data;
  },
};
